import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  OUTLOOK_REFUSALS,
  type MailFolder,
  type MySyncedFolder,
  type OutlookFolderLink,
  type OutlookPreview,
  type OutlookStatus,
} from "@gb/contracts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useChooseOutlookFolders,
  useConfirmMicrosoft,
  useForgetMicrosoft,
  useMailFolders,
  useMySyncedFolders,
  usePreviewOutlookFolders,
  useStopOutlookFolder,
} from "@/features/project/outlook-api";
import { cn } from "@/lib/utils";
import { whose } from "./outlook-line";

/**
 * The project's Outlook folders (`plans/outlook-sync.md`): confirm the
 * viewer's Microsoft account, choose folders of their own mailbox, and stop
 * any folder.
 *
 * The dialog says plainly how it works (the owner's decision of 2026-10-16,
 * question 27): GroundBrain may read mail across the organisation, it reads
 * only the folders people choose, a person can choose only from their own
 * mailbox, and everyone on the project sees what comes in.
 *
 * Anyone on the project sees the folders and may stop one, account or not
 * (2026-10-18, QA finding 1): a folder bringing in the wrong mail must not
 * wait for its owner. Only choosing needs the viewer's own account. With no
 * folder and no account there is nothing to manage, so it opens on
 * "Confirm with Microsoft".
 */
export function OutlookDialog({
  projectId,
  status,
  onClose,
}: {
  projectId: string;
  status: OutlookStatus;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"manage" | "choose">(
    !status.account && status.folders.length === 0 ? "choose" : "manage",
  );
  const toManage = status.folders.length > 0 ? () => setMode("manage") : onClose;
  // Nothing can be chosen while the deployment or the organisation is not set
  // up, and the dialog says so rather than that the organisation allows it
  // (2026-10-18, QA finding 4).
  const blocked = !status.configured
    ? OUTLOOK_REFUSALS.notConfigured
    : !status.connected
      ? OUTLOOK_REFUSALS.notConnected
      : null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        {mode === "choose" && !status.account ? (
          <Confirm blocked={blocked} onBack={toManage} />
        ) : mode === "choose" ? (
          <Choose projectId={projectId} onBack={() => setMode("manage")} onDone={() => setMode("manage")} />
        ) : (
          <Manage
            projectId={projectId}
            status={status}
            blocked={blocked}
            onChoose={() => setMode("choose")}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** "Confirm with Microsoft" (question 3), before choosing anything. */
function Confirm({ blocked, onBack }: { blocked: string | null; onBack: () => void }) {
  const confirm = useConfirmMicrosoft();
  return (
    <>
      <DialogHeader>
        <DialogTitle>Sync your Outlook folders</DialogTitle>
        <DialogCloseButton />
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-3">
          <DialogDescription>
            First, GroundBrain needs to know which mailbox is yours, so that you can choose only your own folders.
            Microsoft asks you to sign in and tells GroundBrain who you are. It asks for nothing else.
          </DialogDescription>
          {blocked ? (
            <p role="alert" className="text-[0.85rem] font-semibold text-danger">
              {blocked}
            </p>
          ) : (
            <p className="text-[0.82rem] text-muted">
              Your organisation lets GroundBrain read mail. GroundBrain reads only the folders people choose here.
            </p>
          )}
          {confirm.error && (
            <p role="alert" className="text-[0.85rem] font-semibold text-danger">
              {confirm.error.message}
            </p>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onBack} disabled={confirm.isPending}>
          Cancel
        </Button>
        <Button
          className="hover:bg-brand-deep"
          disabled={confirm.isPending || blocked !== null}
          onClick={() => confirm.mutate()}
        >
          {confirm.isPending && <Loader2 size={16} className="animate-spin" />}
          Confirm with Microsoft
        </Button>
      </DialogFooter>
    </>
  );
}

function Manage({
  projectId,
  status,
  blocked,
  onChoose,
  onClose,
}: {
  projectId: string;
  status: OutlookStatus;
  blocked: string | null;
  onChoose: () => void;
  onClose: () => void;
}) {
  const [showMine, setShowMine] = useState(false);
  return (
    <>
      <DialogHeader>
        <DialogTitle>Outlook folders</DialogTitle>
        <DialogCloseButton />
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-4">
          <DialogDescription>
            Every email in these folders, and every email that arrives in them later, comes into this project.
            Everyone on this project in GroundBrain sees them.
          </DialogDescription>
          {blocked && (
            <p role="alert" className="text-[0.85rem] font-semibold text-danger">
              {blocked} Until then these folders don’t sync.
            </p>
          )}
          {status.folders.length === 0 ? (
            <p className="text-[0.86rem] text-ink-slate">No folder syncs into this project yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border rounded-card-lg border-[1.5px] border-border">
              {status.folders.map((folder) => (
                <FolderRow key={folder.id} projectId={projectId} folder={folder} />
              ))}
            </ul>
          )}
          {status.account ? (
            <>
              <Account account={status.account} />
              <div>
                <button
                  type="button"
                  aria-expanded={showMine}
                  onClick={() => setShowMine((was) => !was)}
                  className="cursor-pointer text-[0.82rem] font-semibold text-muted underline-offset-2 hover:text-ink hover:underline"
                >
                  Your folders in every project
                </button>
                {showMine && <MyFolders />}
              </div>
            </>
          ) : (
            <p className="text-[0.82rem] text-muted">
              To choose folders of your own mailbox, confirm your Microsoft account first.
            </p>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onChoose} disabled={blocked !== null}>
          Choose folders from your mailbox
        </Button>
        <Button className="hover:bg-brand-deep" onClick={onClose}>
          Done
        </Button>
      </DialogFooter>
    </>
  );
}

/** One folder of the project, and "Stop syncing", asked twice. Anyone on the project may stop it. */
function FolderRow({ projectId, folder }: { projectId: string; folder: OutlookFolderLink }) {
  const stop = useStopOutlookFolder();
  const [asking, setAsking] = useState(false);
  return (
    <li className="flex flex-col gap-1 px-4 py-3 text-[0.86rem]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-ink">{folder.folderName}</span>
        <span className="text-muted">{folder.path}</span>
        <span className="ml-auto text-ink-slate">
          {folder.emails === 1 ? "1 email" : `${folder.emails} emails`}
          {folder.waiting > 0 && `, ${folder.waiting} on the way`}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={cn("text-[0.82rem]", folder.health === "failing" ? "font-semibold text-danger" : "text-muted")}>
          {whose(folder).replace(/^./, (first) => first.toUpperCase())}
          {folder.health === "failing" && folder.failure ? ` · ${folder.failure}` : ""}
        </span>
        {asking ? (
          <span className="ml-auto flex items-center gap-2">
            <span className="text-[0.82rem] text-ink-slate">Its emails stay in the project.</span>
            <Button variant="secondary" size="sm" onClick={() => setAsking(false)} disabled={stop.isPending}>
              Back
            </Button>
            <Button
              size="sm"
              className="hover:bg-brand-deep"
              disabled={stop.isPending}
              onClick={() => stop.mutate({ projectId, linkId: folder.id })}
            >
              {stop.isPending && <Loader2 size={14} className="animate-spin" />}
              Stop syncing
            </Button>
          </span>
        ) : (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setAsking(true)}>
            Stop syncing
          </Button>
        )}
      </div>
      {stop.error && (
        <p role="alert" className="text-[0.82rem] font-semibold text-danger">
          {stop.error.message}
        </p>
      )}
    </li>
  );
}

/** Who the viewer confirmed as, and forgetting it: every folder of it stops, in every project. */
function Account({ account }: { account: NonNullable<OutlookStatus["account"]> }) {
  const forget = useForgetMicrosoft();
  const [asking, setAsking] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.82rem] text-muted">
      <span>
        Confirmed as <span className="font-semibold text-ink-slate">{account.address ?? account.name}</span>
      </span>
      {asking ? (
        <span className="flex items-center gap-2">
          <span>Every folder of your mailbox stops syncing, in every project. Their emails stay.</span>
          <Button variant="secondary" size="sm" onClick={() => setAsking(false)} disabled={forget.isPending}>
            Back
          </Button>
          <Button size="sm" disabled={forget.isPending} onClick={() => forget.mutate()}>
            Forget my account
          </Button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setAsking(true)}
          className="cursor-pointer underline-offset-2 hover:text-ink hover:underline"
        >
          Forget my Microsoft account
        </button>
      )}
      {forget.error && (
        <p role="alert" className="w-full font-semibold text-danger">
          {forget.error.message}
        </p>
      )}
    </div>
  );
}

/** Every folder of the viewer's mailbox that syncs, in every project (question 27). */
function MyFolders() {
  const mine = useMySyncedFolders();
  if (mine.isPending) return <Loader2 size={16} className="mt-2 animate-spin text-muted" />;
  if (mine.error) return <p className="mt-2 text-[0.82rem] text-danger">{mine.error.message}</p>;
  if (mine.data.length === 0) {
    return <p className="mt-2 text-[0.82rem] text-ink-slate">No folder of your mailbox syncs anywhere.</p>;
  }
  return (
    <ul className="mt-2 flex flex-col gap-1.5 text-[0.82rem] text-ink-slate">
      {mine.data.map((folder) => (
        <MyFolderRow key={folder.id} folder={folder} />
      ))}
    </ul>
  );
}

/**
 * One of them, stopped only once asked twice, as the project's own rows are
 * (2026-10-18, QA finding 8): this list holds other projects' folders too.
 */
function MyFolderRow({ folder }: { folder: MySyncedFolder }) {
  const stop = useStopOutlookFolder();
  const [asking, setAsking] = useState(false);
  return (
    <li className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-ink">{folder.folderName}</span>
        <span className="text-muted">into “{folder.projectName}”</span>
        {asking ? (
          <span className="ml-auto flex items-center gap-2">
            <span>Its emails stay in “{folder.projectName}”.</span>
            <Button variant="secondary" size="sm" onClick={() => setAsking(false)} disabled={stop.isPending}>
              Back
            </Button>
            <Button
              size="sm"
              className="hover:bg-brand-deep"
              disabled={stop.isPending}
              onClick={() => stop.mutate({ projectId: folder.projectId, linkId: folder.id })}
            >
              {stop.isPending && <Loader2 size={14} className="animate-spin" />}
              Stop syncing
            </Button>
          </span>
        ) : (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setAsking(true)}>
            Stop syncing
          </Button>
        )}
      </div>
      {stop.error && (
        <p role="alert" className="font-semibold text-danger">
          {stop.error.message}
        </p>
      )}
    </li>
  );
}

/** The picker of the viewer's own mailbox (question 10), then what the chosen folders hold (question 13). */
function Choose({ projectId, onBack, onDone }: { projectId: string; onBack: () => void; onDone: () => void }) {
  const folders = useMailFolders(true);
  const preview = usePreviewOutlookFolders(projectId);
  const choose = useChooseOutlookFolders(projectId);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [found, setFound] = useState<OutlookPreview | null>(null);
  const error = folders.error?.message ?? preview.error?.message ?? choose.error?.message ?? null;
  const busy = preview.isPending || choose.isPending;

  const toggle = (id: string) => {
    setFound(null);
    setPicked((before) => {
      const next = new Set(before);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Choose folders from your mailbox</DialogTitle>
        <DialogCloseButton />
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-3">
          <DialogDescription>
            Choose folders from your own mailbox. Every email in them, and every email that arrives later, comes
            into this project. Everyone on this project in GroundBrain will see them.
          </DialogDescription>
          <p className="text-[0.82rem] text-muted">
            Your organisation lets GroundBrain read mail. GroundBrain reads only the folders people choose here.
            A folder’s subfolders come with it.
          </p>
          {/* Above the list, so it is seen before "Sync these folders": the
              list scrolls, and a count below it was cut off (2026-10-17). */}
          {found && (
            <div className="rounded-card-lg border-[1.5px] border-border bg-subtle/60 px-4 py-3 text-[0.86rem]">
              <p className="font-semibold text-ink">{found.folders.map((folder) => folder.path).join(", ")}</p>
              <p className="mt-1 text-ink-slate">
                {found.emails === 1 ? "1 email" : `${found.emails} emails`}
                {found.subfolders > 0
                  ? `, with ${found.subfolders === 1 ? "1 subfolder" : `${found.subfolders} subfolders`}.`
                  : "."}{" "}
                {found.emails === 1
                  ? "Everyone on this project will see it."
                  : "Everyone on this project will see them."}
              </p>
            </div>
          )}
          {folders.isPending ? (
            <Loader2 size={18} className="animate-spin text-muted" />
          ) : folders.data ? (
            <FolderTree folders={folders.data.folders} picked={picked} onToggle={toggle} />
          ) : null}
          {error && (
            <p role="alert" className="text-[0.85rem] font-semibold text-danger">
              {error}
            </p>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onBack} disabled={choose.isPending}>
          Back
        </Button>
        {found ? (
          <Button
            className="hover:bg-brand-deep"
            disabled={busy}
            onClick={() => void choose.mutateAsync([...picked]).then(onDone).catch(() => undefined)}
          >
            {choose.isPending && <Loader2 size={16} className="animate-spin" />}
            Sync these folders
          </Button>
        ) : (
          <Button
            className="hover:bg-brand-deep"
            disabled={busy || picked.size === 0}
            onClick={() => void preview.mutateAsync([...picked]).then(setFound).catch(() => undefined)}
          >
            {preview.isPending && <Loader2 size={16} className="animate-spin" />}
            Check these folders
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

/** The mailbox's folders as a tree, each with a tick box, or the reason it cannot be chosen. */
function FolderTree({
  folders,
  picked,
  onToggle,
}: {
  folders: MailFolder[];
  picked: Set<string>;
  onToggle: (id: string) => void;
}) {
  const rows = useMemo(() => inTreeOrder(folders), [folders]);
  return (
    <ul className="max-h-80 overflow-y-auto rounded-card-lg border-[1.5px] border-border py-1" aria-label="Your mailbox">
      {rows.map(({ folder, depth }) => {
        const blocked = folder.refusal ?? (folder.syncsInto ? `Syncs into “${folder.syncsInto.projectName}”.` : null);
        const id = `outlook-folder-${folder.id}`;
        return (
          <li
            key={folder.id}
            className="flex items-start gap-2 px-3 py-1.5 text-[0.86rem]"
            style={{ paddingLeft: `${12 + depth * 18}px` }}
          >
            <input
              id={id}
              type="checkbox"
              className="mt-1"
              checked={picked.has(folder.id)}
              disabled={blocked !== null}
              onChange={() => onToggle(folder.id)}
            />
            <label htmlFor={id} className={cn("flex min-w-0 flex-1 flex-col", blocked ? "text-muted" : "text-ink")}>
              <span>
                <span className="font-semibold">{folder.name}</span>
                <span className="text-muted"> · {folder.emails === 1 ? "1 email" : `${folder.emails} emails`}</span>
              </span>
              {blocked && <span className="text-[0.78rem]">{blocked}</span>}
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/** Parents before their children, each child under its parent, with its depth. */
function inTreeOrder(folders: MailFolder[]): { folder: MailFolder; depth: number }[] {
  const children = new Map<string | null, MailFolder[]>();
  const known = new Set(folders.map((folder) => folder.id));
  for (const folder of folders) {
    const parent = folder.parentId && known.has(folder.parentId) ? folder.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), folder]);
  }
  const out: { folder: MailFolder; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const folder of children.get(parent) ?? []) {
      out.push({ folder, depth });
      if (depth < 20) walk(folder.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
